import { config } from 'src/utils/config';
import { FetchResult } from 'src/types/TApiQueries';
import { getStandardHeaders } from 'src/api/utils/helpers';

export type PrintExportParams = {
  format?: string;
  cmyk_profile?: string;
  rendering_intent?: string;
  dpi?: number;
  target_size?: string;
  allow_upscale?: boolean;
};

export type PrintExportDisposition = 'add_conversion' | 'add_new_asset';

const PRINT_FILE_EXTENSIONS: Record<string, string> = {
  tiff: '.tiff',
  jpeg: '.jpg',
  pdf: '.pdf',
  pdfx: '.pdf',
};

const buildPrintExportQuery = (mode: string, params: PrintExportParams, extra?: Record<string, string | undefined>) => {
  const qs = new URLSearchParams();
  qs.set('mode', mode);
  if (params.format) qs.set('format', params.format);
  if (params.cmyk_profile) qs.set('cmyk_profile', params.cmyk_profile);
  if (params.rendering_intent) qs.set('rendering_intent', params.rendering_intent);
  if (params.dpi != null) qs.set('dpi', String(params.dpi));
  if (params.target_size) qs.set('target_size', params.target_size);
  if (params.allow_upscale) qs.set('allow_upscale', 'true');
  if (extra) {
    Object.entries(extra).forEach(([k, v]) => {
      if (v != null && v !== '') qs.set(k, v);
    });
  }
  return qs.toString();
};

/**
 * Generate a print-ready file (mode=get_image) and trigger a browser
 * download. Synchronous stream — no job / notification. Returns
 * `{ ok }` or `{ ok: false, error }` (error carries `status` + parsed body
 * for 402 quota/credits and 403 not-enabled).
 */
export async function downloadPrintExport(
  assetId: string,
  assetName: string,
  params: PrintExportParams,
  accessToken: string,
): Promise<{ ok: boolean; error?: any }> {
  const query = buildPrintExportQuery('get_image', params);
  const url = `${config.hostUrl}/api/v1/assets/${assetId}/addons/print_export?${query}`;
  try {
    const response = await fetch(url, { headers: getStandardHeaders(accessToken) });
    if (!response.ok) {
      let body: any = null;
      try {
        body = await response.json();
      } catch {
        // non-JSON error body
      }
      return { ok: false, error: { status: response.status, data: body } };
    }
    const blob = await response.blob();
    const ext = PRINT_FILE_EXTENSIONS[params.format || 'tiff'] || '';
    const base = (assetName || 'print_export').replace(/\.[^/.]+$/, '');
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = `${base}_print${ext}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(objectUrl);
    return { ok: true };
  } catch (err) {
    return { ok: false, error: { message: (err as Error)?.message } };
  }
}

/**
 * Queue an async print export save (mode=save_image). Returns the created
 * job id (the backend responds 202). The PRINT_EXPORT job appears on /jobs
 * and a completion notification fires when done.
 */
export async function savePrintExport(
  assetId: string,
  params: PrintExportParams,
  disposition: PrintExportDisposition,
  options: { conversion_name?: string; inherit_metadata?: boolean },
  accessToken: string,
): Promise<{ ok: boolean; jobId?: string; error?: any }> {
  const extra: Record<string, string | undefined> = { disposition };
  if (disposition === 'add_conversion') {
    extra.conversion_name = options.conversion_name || 'print_ready';
  }
  if (disposition === 'add_new_asset' && options.inherit_metadata) {
    extra.metadata = 'inherit';
  }
  const query = buildPrintExportQuery('save_image', params, extra);
  const url = `${config.hostUrl}/api/v1/assets/${assetId}/addons/print_export?${query}`;
  try {
    const response = await fetch(url, { headers: getStandardHeaders(accessToken) });
    let body: any = null;
    try {
      body = await response.json();
    } catch {
      // ignore parse error
    }
    if (!response.ok) {
      return { ok: false, error: { status: response.status, data: body } };
    }
    return { ok: true, jobId: body?.job_id };
  } catch (err) {
    return { ok: false, error: { message: (err as Error)?.message } };
  }
}

export type BulkCutoutRequest = {
  ids: string[];
  /** 'transparent' | 'white' — see cutoutOutcomes. */
  background: string;
  /** 'add_new_asset' (default) | 'add_conversion' */
  disposition?: string;
  /** Required when disposition is add_conversion. */
  conversion_name?: string;
  /** World-readable result. Defaults to true server-side. */
  public?: boolean;
};

export type BulkCutoutQueued = {
  job_id: number;
  count: number;
  background: string;
  credits_required: number;
};

/**
 * Queue a cut-out over a whole selection.
 *
 * Returns the job id; the work runs server-side, one asset at a time, and
 * progress + per-asset outcomes are read from `GET /api/v1/jobs/{job_id}`.
 *
 * Throws with `status` attached so the caller can tell the three refusals
 * apart: 402 (the wallet cannot cover the whole batch), 409 (this user already
 * has a batch running) and 403 (the addon is not enabled).
 */
export async function bulkCutout(
  request: BulkCutoutRequest,
  accessToken: string,
): Promise<BulkCutoutQueued> {
  const response = await fetch(`${config.hostUrl}/api/v1/assets/bulk/bg_remove`, {
    method: 'POST',
    headers: { ...getStandardHeaders(accessToken), 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error: any = new Error(body?.message || body?.data?.message || 'Background removal failed');
    error.status = response.status;
    error.body = body;
    throw error;
  }
  return body as BulkCutoutQueued;
}

export type CutoutResult = {
  /** `data:<mime>;base64,...` — ready to hand straight to an <img src>. */
  dataUrl: string;
  /** Payload bytes, base64 only (no data: prefix), for re-uploading on save. */
  base64: string;
  /** The real type the server sent. PNG with alpha for a transparent cut-out. */
  contentType: string;
};

/**
 * Run the background-removal addon and return the cut-out.
 *
 * `background` picks the outcome: `transparent` leaves the alpha channel,
 * `white` flattens onto white server-side (see
 * `filespin/lib/imaging/storage.py:flatten_alpha_onto_colour`). Both are one
 * provider call, so both cost the same 2 credits.
 *
 * The content type is read from the response rather than assumed. The previous
 * caller hardcoded `image/jpeg`, which silently discarded the alpha channel of
 * the very thing it had just cut out.
 */
export async function getCutout(
  assetId: string,
  background: string,
  accessToken: string,
): Promise<CutoutResult> {
  const url =
    `${config.hostUrl}/api/v1/assets/${assetId}/addons/bg_remove` +
    `?mode=get_image&background=${encodeURIComponent(background)}`;
  const response = await fetch(url, { headers: getStandardHeaders(accessToken) });
  if (!response.ok) {
    // Let the caller distinguish 402 (credits) and 403 (addon off) from a
    // generic failure; the body carries the detail.
    const error: any = new Error(`Background removal failed (${response.status})`);
    error.status = response.status;
    try {
      error.body = await response.json();
    } catch {
      /* non-JSON error body */
    }
    throw error;
  }

  const blob = await response.blob();
  const contentType = blob.type || response.headers.get('content-type') || 'image/png';
  const dataUrl = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result as string;
      if (result) resolve(result);
      else reject(new Error('FileReader result is null'));
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

  return { dataUrl, base64: dataUrl.split(',')[1], contentType };
}

